import {
  addGameObject,
  anchoredPos,
  getEntityState,
  getEntityStateAndProgress,
  getZeroStaticEntity,
  setEntityPosition,
  setParent,
} from '../entity';
import { debugState, dt } from '../index';
import { executeCommand, setVisibilityState } from '../simulate';
import { autoTypeSpeed, SHAPE_STYLES, TEXT_STYLE } from '../constants';
import { COLORS } from '../constants-colors';
import {
  addV2,
  cloneTextStyle,
  Ease,
  scaleV2,
  remapNumber01,
  removeDenseArrayIndex,
  v2,
  cloneShapeStyle,
} from '../helpers';
import { setContainerStyle } from '../container';
import {
  ServerEvent_EntityProps,
  ServerEvent_TextObject,
  ObjectType,
  Alignment_Horizontal,
} from '../shared/api_shared_types';
import {
  AutoTypeController,
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  MoveMode,
  RenderShapeStyle,
  Shape,
  TextAppearStyle,
  TextBlock,
  TextLine,
  TextObject,
  TextObjectStyle,
  V2,
  Word,
  WordType,
} from '../types';
import { createDomObject } from '../dom_object';
import logger from '../controllers/logger';
import * as TextLayoutController from '../controllers/text_layout_controller';

import * as text_parser from './text_parser';
import * as text_renderer from './textobject_render';
import { createAndAddLocalTextObject2 } from '../text_object2/text_object2';

export function controller(): GameObjectController {
  return {
    getNew: getZeroTextObject,
    sync,
    update,
    createPackage: text_renderer.createPackage,
  };
}

export function getZeroTextObject(ent: Entity) {
  let newTextObject: TextObject = {
    ...ent,
    objectType: ObjectType.TEXTOBJECT,

    text: '',
    words: [],

    hoverContainers: [],

    closeBtn: null,

    textStyle: cloneTextStyle(TEXT_STYLE.DEFAULT), //should be null
    removeDoubleWhiteSpaces: false,
    removeEmptyLinesOnEnd: false,

    maxWidthEm: 1,
    maxWidthPx: 0,
    minWidthMod: 1,

    fontSizePx: 0,
    maxLines: 0,

    textEnt: null,

    changeDuration: 0.3,

    question: false,

    currentTextFade: 1,

    hoverWords: false,
    allowEdit: false,

    hideAllText: false,
    textChanged: false,
    textShadow: null,
    onlyShowInDefaultState: true,

    appearStyle: TextAppearStyle.NONE,
    shapeStyle: null,
    backgroundAlpha: 255,

    autoTypeController: null,

    linkPopup: null,
    linkPopupDisappearTime: 0,
  };

  return newTextObject;
}

function sync(manager: GameManager, textObject: TextObject, data: ServerEvent_TextObject) {
  let entityProps: ServerEvent_EntityProps = {};

  const TEST_NEW_TEXT_OJECT = false;
  if (TEST_NEW_TEXT_OJECT) {
    const text2 = createAndAddLocalTextObject2(manager, getZeroStaticEntity(), {
      text: data.text[0],
    });
    manager.mainRoom.canvas.attachChild(text2, manager.mainRoom.canvas);
    setEntityPosition(text2, 0, 100);
  }

  if (data.hasOwnProperty('entityProps')) {
    entityProps = data.entityProps;
  }

  if (!textObject.id) {
    //create textObject
    let maxWidthEm = 6;

    let style: TextObjectStyle = { ...TEXT_STYLE.DEFAULT_PANEL };
    let shapeStyle: RenderShapeStyle = null;
    let question = false;
    let appearStyle = TextAppearStyle.AUTO_TYPE;

    if (data.hasOwnProperty('style') || entityProps.hasOwnProperty('style')) {
      const _style = data.style.length > 0 ? data.style : entityProps.style;

      switch (_style) {
        case 'dark_on_light':
          style = { ...TEXT_STYLE.DARK_ON_LIGHT };

          break;

        case 'content_default':
          style = { ...TEXT_STYLE.CONTENT_DEFAULT };
          break;

        case 'content_small':
          style = { ...TEXT_STYLE.CONTENT_DEFAULT_SMALL };
          break;

        case 'text_block':
          style = { ...TEXT_STYLE.TEXT_BLOCK };
          shapeStyle = { ...SHAPE_STYLES.TEXT_OUTLINE };
          textObject.influenceKinetic = true;
          break;

        case 'floating_item':
          style = { ...TEXT_STYLE.FLOATING };
          textObject.moveMode = MoveMode.KINETIC;
          textObject.influenceKinetic = true;
          break;

        case 'floating_item_light':
          style = { ...TEXT_STYLE.FLOATING_LIGHT };
          textObject.moveMode = MoveMode.KINETIC;
          textObject.influenceKinetic = true;
          break;

        case 'floating_item_highlight':
          style = { ...TEXT_STYLE.FLOATING_HL };
          shapeStyle = { ...SHAPE_STYLES.TEXT_HIGHLIGHT };
          textObject.moveMode = MoveMode.KINETIC;
          textObject.influenceKinetic = true;
          break;

        case 'floating_item_rounded':
          style = { ...TEXT_STYLE.FLOATING_ROUNDED };
          textObject.moveMode = MoveMode.KINETIC;
          textObject.influenceKinetic = true;
          break;

        case 'floating_item_light_rounded':
          style = { ...TEXT_STYLE.FLOATING_LIGHT_ROUNDED };
          textObject.moveMode = MoveMode.KINETIC;
          textObject.influenceKinetic = true;
          break;

        case 'floating_item_highlight_rounded':
          style = { ...TEXT_STYLE.FLOATING_HL_ROUNDED };
          shapeStyle = { ...SHAPE_STYLES.TEXT_HIGHLIGHT };
          textObject.moveMode = MoveMode.KINETIC;
          textObject.influenceKinetic = true;
          break;

        case 'floating_item_small':
          style = { ...TEXT_STYLE.FLOATING_SMALL };
          textObject.moveMode = MoveMode.KINETIC;
          textObject.influenceKinetic = true;
          if (
            data.entityProps &&
            data.entityProps.moveMode &&
            data.entityProps.moveMode === 'static'
          ) {
            style.colorHighlight = style.color;
            textObject.influenceKinetic = false;
            textObject.allowHighlight = false;
          }
          maxWidthEm = 2.4;
          break;

        case 'floating_item_green':
          style = { ...TEXT_STYLE.FLOATING_GREEN };
          textObject.moveMode = MoveMode.KINETIC;
          textObject.influenceKinetic = true;
          break;

        case 'floating_item_red':
          style = { ...TEXT_STYLE.FLOATING_RED };
          textObject.moveMode = MoveMode.KINETIC;
          textObject.influenceKinetic = true;
          break;

        case 'title':
          style = { ...TEXT_STYLE.CONTENT_TITLE };
          maxWidthEm = 8;
          appearStyle = TextAppearStyle.FADE;
          textObject.onlyShowInDefaultState = false;
          break;

        case 'subtitle':
          style = { ...TEXT_STYLE.CONTENT_SUBTITLE };
          maxWidthEm = 8;
          break;

        case 'subtitle_soft':
          style = { ...TEXT_STYLE.CONTENT_SUBTITLE_SOFT };
          maxWidthEm = 8;
          break;

        case 'title_big':
          style = { ...TEXT_STYLE.CONTENT_TITLE_BIG };
          maxWidthEm = 8;
          appearStyle = TextAppearStyle.FADE;
          textObject.onlyShowInDefaultState = false;
          break;

        case 'title_mission':
          style = { ...TEXT_STYLE.CONTENT_TITLE_MISSION };
          maxWidthEm = 20;
          appearStyle = TextAppearStyle.FADE;
          textObject.onlyShowInDefaultState = false;
          break;

        case 'question':
          style = { ...TEXT_STYLE.FLOATING_QUESTION_NEW };
          shapeStyle = SHAPE_STYLES.OUTLINE_THIN;
          textObject.moveMode = MoveMode.KINETIC;
          textObject.influenceKinetic = true;
          question = true;

          break;
        case 'default':
          style = { ...TEXT_STYLE.DEFAULT_NEW };

          break;
        case 'nmtp':
          style = { ...TEXT_STYLE.NMTP };
          maxWidthEm = 5;

          break;
        case 'default_panel':
          style = { ...TEXT_STYLE.DEFAULT_PANEL };

          break;
        case 'light_on_dark':
          style = { ...TEXT_STYLE.LIGHT_ON_DARK };

          break;
        case 'canvas_default':
          style = { ...TEXT_STYLE.CANVAS_DEFAULT };
          break;
        case 'home':
          style = { ...TEXT_STYLE.HOME };

          maxWidthEm = 8;

          break;
        case 'light_on_dark_big':
          style = { ...TEXT_STYLE.CONTENT_TITLE };

          maxWidthEm = 8;

          break;

        case 'light_on_dark_small':
          style = { ...TEXT_STYLE.LIGHT_ON_DARK_SMALL };

          break;

        case 'reframe_negative':
          style = { ...TEXT_STYLE.REFRAME_NEGATIVE };
          style.textAlign = Alignment_Horizontal.LEFT;

          break;

        case 'reframe_positive':
          style = { ...TEXT_STYLE.REFRAME_POSITIVE };
          style.textAlign = Alignment_Horizontal.LEFT;

          break;

        default:
      }
    }

    if (data.hasOwnProperty('align')) {
      style.textAlign = data.align;
    }

    if (data.hasOwnProperty('fixedFontSize')) {
      style.doAutoResize = !data.fixedFontSize;
    }

    if (data.hasOwnProperty('widthEm') && data.widthEm > 0) {
      maxWidthEm = data.widthEm;
    }

    if (shapeStyle) {
      textObject.shapeStyle = cloneShapeStyle(shapeStyle);
    }

    // textObject.appearStyle = TextAppearStyle.AUTO_TYPE;

    textObject.removeDoubleWhiteSpaces = true;
    textObject.removeEmptyLinesOnEnd = true;

    addGameObject(manager, textObject);
    initTextObject(
      manager,
      textObject,
      data.text[0],
      cloneTextStyle(style),
      maxWidthEm,
      appearStyle,
    );
    textObject.hoverWords = true;

    textObject.question = question;

    // setParentByEntityProps(manager, textObject, data.sId, data.roomId, entityProps);
  } else {
    if (data.hasOwnProperty('style') || entityProps.hasOwnProperty('style')) {
      let maxWidthEm = 6;

      let style: TextObjectStyle = { ...TEXT_STYLE.DEFAULT_PANEL };
      let shapeStyle: RenderShapeStyle = null;

      const _style = data.style.length > 0 ? data.style : entityProps.style;

      switch (_style) {
        case 'dark_on_light':
          style = { ...TEXT_STYLE.DARK_ON_LIGHT };

          break;

        case 'content_default':
          style = { ...TEXT_STYLE.CONTENT_DEFAULT };
          break;

        case 'content_small':
          style = { ...TEXT_STYLE.CONTENT_DEFAULT_SMALL };
          break;

        case 'text_block':
          style = { ...TEXT_STYLE.TEXT_BLOCK };
          shapeStyle = { ...SHAPE_STYLES.TEXT_OUTLINE };
          break;

        case 'floating_item':
          style = { ...TEXT_STYLE.FLOATING };
          break;

        case 'floating_item_light':
          style = { ...TEXT_STYLE.FLOATING_LIGHT };
          break;

        case 'floating_item_highlight':
          style = { ...TEXT_STYLE.FLOATING_HL };
          shapeStyle = { ...SHAPE_STYLES.TEXT_HIGHLIGHT };
          break;

        case 'floating_item_small':
          style = { ...TEXT_STYLE.FLOATING_SMALL };
          maxWidthEm = 2.4;
          break;

        case 'floating_item_green':
          style = { ...TEXT_STYLE.FLOATING_GREEN };
          break;

        case 'floating_item_red':
          style = { ...TEXT_STYLE.FLOATING_RED };
          break;

        case 'title':
          style = { ...TEXT_STYLE.CONTENT_TITLE };
          maxWidthEm = 8;
          break;

        case 'subtitle':
          style = { ...TEXT_STYLE.CONTENT_SUBTITLE };
          maxWidthEm = 8;
          break;

        case 'subtitle_soft':
          style = { ...TEXT_STYLE.CONTENT_SUBTITLE_SOFT };
          maxWidthEm = 8;
          break;

        case 'title_big':
          style = { ...TEXT_STYLE.CONTENT_TITLE_BIG };
          maxWidthEm = 8;
          break;

        case 'title_mission':
          style = { ...TEXT_STYLE.CONTENT_TITLE_MISSION };
          maxWidthEm = 20;
          break;

        case 'question':
          style = { ...TEXT_STYLE.FLOATING_QUESTION };

          break;
        case 'default':
          style = { ...TEXT_STYLE.DEFAULT_NEW };

          break;
        case 'nmtp':
          style = { ...TEXT_STYLE.NMTP };
          maxWidthEm = 5;

          break;
        case 'default_panel':
          style = { ...TEXT_STYLE.DEFAULT_PANEL };

          break;
        case 'light_on_dark':
          style = { ...TEXT_STYLE.LIGHT_ON_DARK };

          break;
        case 'canvas_default':
          style = { ...TEXT_STYLE.CANVAS_DEFAULT };
          break;
        case 'home':
          style = { ...TEXT_STYLE.HOME };

          break;
        case 'light_on_dark_big':
          style = { ...TEXT_STYLE.CONTENT_TITLE };

          break;

        case 'light_on_dark_small':
          style = { ...TEXT_STYLE.LIGHT_ON_DARK_SMALL };

          break;

        case 'reframe_negative':
          style = { ...TEXT_STYLE.REFRAME_NEGATIVE };
          style.textAlign = Alignment_Horizontal.LEFT;

          break;

        case 'reframe_positive':
          style = { ...TEXT_STYLE.REFRAME_POSITIVE };
          style.textAlign = Alignment_Horizontal.LEFT;

          break;
        case 'floating_item_rounded':
          style = { ...TEXT_STYLE.FLOATING_ROUNDED };
          // textObject.moveMode = MoveMode.KINETIC;
          // textObject.influenceKinetic = true;
          break;

        case 'floating_item_light_rounded':
          style = { ...TEXT_STYLE.FLOATING_LIGHT_ROUNDED };
          // textObject.moveMode = MoveMode.KINETIC;
          // textObject.influenceKinetic = true;
          break;

        case 'floating_item_highlight_rounded':
          style = { ...TEXT_STYLE.FLOATING_HL_ROUNDED };
          shapeStyle = { ...SHAPE_STYLES.TEXT_HIGHLIGHT };
          // textObject.moveMode = MoveMode.KINETIC;
          // textObject.influenceKinetic = true;
          break;

        default:
      }

      if (data.hasOwnProperty('fixedFontSize')) {
        style.doAutoResize = !data.fixedFontSize;
      }

      textObject.shapeStyle = shapeStyle;
      textObject.textStyle = cloneTextStyle(style);
    }
  }

  if ('text' in data) {
    if (textObject.text !== data.text[0]) {
      changeTextObjectText(textObject, data.text[0]);
    }
  }

  if (textObject.moveMode === MoveMode.ANCHORED || textObject.moveMode === MoveMode.STATIC) {
    textObject.allowHighlight = false;
    textObject.clickable = false;
  }
}

export function initTextObject(
  manager: GameManager,
  tObj: TextObject,
  text: string,
  style: TextObjectStyle = TEXT_STYLE.DEFAULT,
  maxWidthEm = 3,
  appearStyle = TextAppearStyle.NONE,
): TextObject {
  if (tObj.shape === Shape.NONE) {
    tObj.shape = Shape.RECT;
  }

  tObj.maxWidthEm = maxWidthEm ? maxWidthEm : 3;

  //style
  tObj.textStyle = cloneTextStyle(style);

  if (tObj.disableAutoSize) {
    tObj.textStyle.doAutoResize = false;
  }

  tObj.appearStyle = appearStyle;

  changeTextObjectText(tObj, text);

  const progress = 0;

  switch (tObj.appearStyle) {
    case TextAppearStyle.FADE:
      textFade(tObj, progress);
      tObj.onlyShowInDefaultState = false;
      break;
    case TextAppearStyle.FADE_AND_MOVE_RIGHT:
      textFade(tObj, progress);
      textMove(tObj, progress, v2(20, 0));
      tObj.onlyShowInDefaultState = false;
      break;
    case TextAppearStyle.FADE_AND_MOVE_LEFT:
      textFade(tObj, progress);
      textMove(tObj, progress, v2(-20, 0));
      tObj.onlyShowInDefaultState = false;
      break;
    case TextAppearStyle.FADE_AND_MOVE_UP:
      textFade(tObj, progress);
      textMove(tObj, progress, v2(0, -20));
      tObj.onlyShowInDefaultState = false;
      break;
    case TextAppearStyle.FADE_AND_MOVE_DOWN:
      textFade(tObj, progress);
      textMove(tObj, progress, v2(0, 20));
      tObj.onlyShowInDefaultState = false;
      break;
    default:
      textFade(tObj, 1);
      textMove(tObj, 1, v2());
      appearUnfold(tObj, progress);
      break;
  }

  const blurBackground = false;
  if (blurBackground) {
    let el = document.createElement('div');
    el.classList.add('blur-object', 'blur-on');
    let domObj = createDomObject(getZeroStaticEntity(), el, manager.DOM.blurContainer, manager);
    domObj.defaultUpdate = true;
    setParent(domObj, tObj);
    setVisibilityState(domObj, VisibilityState.DEFAULT);
  }

  setVisibilityState(tObj, VisibilityState.APPEARING);

  tObj.layoutChanged = false;

  return tObj;
}

export function initTextObjectNew(
  manager: GameManager,
  textObject: TextObject,
  text: string,
): TextObject {
  if (textObject.shape === Shape.NONE) {
    textObject.shape = Shape.RECT;
  }

  if (!textObject.maxWidthEm) textObject.maxWidthEm = 3;

  //style

  if (textObject.disableAutoSize) {
    textObject.textStyle.doAutoResize = false;
  }

  changeTextObjectText(textObject, text);

  setVisibilityState(textObject, VisibilityState.APPEARING);

  textObject.layoutChanged = false;

  return textObject;
}

export function createAndAddLocalTextObject(
  manager: GameManager,
  ent: Entity,
  text: string,
  style = TEXT_STYLE.DEFAULT,
  maxWidthEm = 4,
  autoType = false,
  maxLines = 0,
  minWidthMod = 1,
  textAppearStyle = TextAppearStyle.NONE,
): TextObject {
  const tObj = getZeroTextObject(ent);
  tObj.maxLines = maxLines;
  addGameObject(manager, tObj);
  tObj.minWidthMod = minWidthMod;

  if (textAppearStyle === TextAppearStyle.NONE && autoType) {
    textAppearStyle = TextAppearStyle.AUTO_TYPE;
  }

  initTextObject(manager, tObj, text, style, maxWidthEm, textAppearStyle);

  return tObj;
}

export function createAndAddLocalTextObjectNew(
  manager: GameManager,
  ent: Entity,
  props: Partial<TextObject>,
  text: string,
): TextObject {
  const tObj = {
    ...getZeroTextObject(ent),
    ...props,
    ...{ textStyle: cloneTextStyle(props.textStyle) },
  };

  addGameObject(manager, tObj);

  initTextObjectNew(manager, tObj, text);

  return tObj;
}

export function createAutoTypeController(
  tObj: TextObject,
  textArray: string[] = [],
): AutoTypeController {
  //this autioObject is not yet created here
  let speed = autoTypeSpeed;
  let { texts, timeCodes } = parseTextTimeCodes(textArray);

  let ct: AutoTypeController = {
    currentCharCount: 0,
    timeSinceLastCharTyped: 0,
    currentTextIndex: 0,
    textObject: tObj,
    currentTextDone: false,
    timeBetweenChars: speed,
    textArray: texts,
    textTimeArray: timeCodes,
    doKill: false,
  };
  return ct;
}

function parseTextTimeCodes(textArray: string[]): { texts: string[]; timeCodes: number[] } {
  let texts: string[] = [];
  let timeCodes: number[] = [];

  for (let i = 0; i < textArray.length; i++) {
    let text = textArray[i];
    if (!text) continue;
    let timeCodeStartIndex = text.indexOf('[');
    let timeCodeEndIndex = -1;
    if (timeCodeStartIndex >= 0) {
      timeCodeEndIndex = text.indexOf(']');
    }
    if (timeCodeEndIndex >= 0) {
      let timeCodeString = text.substring(timeCodeStartIndex + 1, timeCodeEndIndex);
      let timeInSeconds = getSecondsFromTimeCode(timeCodeString);
      let textWithoutTimeCode = text.substring(timeCodeEndIndex + 1);
      texts.push(textWithoutTimeCode.trim());
      timeCodes.push(timeInSeconds);
    } else {
      texts.push(text);
      timeCodes.push(0);
    }
  }

  return { texts, timeCodes };
}

function getSecondsFromTimeCode(timeCodeString: string): number {
  let time = 0;
  let timeStrings = timeCodeString.split(':');
  let sec = 0;
  let min = 0;
  let hour = 0;
  if (timeStrings.length === 1) {
    sec = Number.parseFloat(timeStrings[0]);
  } else if (timeStrings.length === 2) {
    min = Number.parseFloat(timeStrings[0]);
    sec = Number.parseFloat(timeStrings[1]);
  } else if (timeStrings.length === 3) {
    hour = Number.parseFloat(timeStrings[0]);
    min = Number.parseFloat(timeStrings[1]);
    sec = Number.parseFloat(timeStrings[2]);
  } else {
    return 0;
  }
  time = hour * 3600 + min * 60 + sec;
  return Number.isNaN(time) ? 0 : time;
}

function getWordOfType(type: WordType): Word {
  let word = getZeroWord();
  word.type = type;
  return word;
}

function getZeroWord(): Word {
  return {
    type: WordType.none,
    text: '',
    visibleText: '',
    localPos: v2(),
    pos: v2(),
    offset: v2(),
    width: 0,
    height: 0,
    textColor: [0, 0, 0],
    colorAlpha: 255,
    wordStyle: null,
    clickActionId: '',
    hover: false,
    command: null,
  };
}

export function changeTextObjectText(tObj: TextObject, text: string, skipAutoType = false) {
  const isVisibleText = tObj.appearStyle !== TextAppearStyle.AUTO_TYPE || skipAutoType;

  if (!tObj.textEnt || text !== tObj.text) {
    tObj.words = text_parser.parseText(tObj, text, isVisibleText);
  }

  TextLayoutController.layoutTextObject(tObj);

  if (tObj.appearStyle === TextAppearStyle.AUTO_TYPE && !isVisibleText) {
    startAutoTypeController(tObj);
  }

  tObj.isDirty = true;
}

export function recalculateWordSizes(tObj: TextObject) {
  changeTextObjectText(tObj, tObj.text, true);
}

export function updateAutoTypeControllers(controllers: AutoTypeController[], manager: GameManager) {
  const length = controllers.length;

  for (let i = length - 1; i >= 0; i--) {
    let controller = controllers[i];

    const state = getEntityState(controller.textObject);
    if (state !== VisibilityState.DEFAULT) continue;

    if (controller.doKill) {
      removeDenseArrayIndex(controllers, i);
      continue;
    }

    updateAutoTypeController(controller, manager);

    if (controller.doKill) {
      removeDenseArrayIndex(controllers, i);
    }
  }
}

export function updateAutoTypeController(controller: AutoTypeController, manager: GameManager) {
  let tObj = controller.textObject;

  controller.timeSinceLastCharTyped += dt * (debugState.isDebugMode ? 5 : 1);

  let currentCharIndex = 0;
  let noTimeLeft = false;

  //

  const wordLength = tObj.words.length;

  if (controller.timeSinceLastCharTyped < controller.timeBetweenChars) return;
  if (controller.currentTextDone) return;

  for (let w = 0; w < wordLength; w++) {
    let word = tObj.words[w];

    if (word.text.length === 0) continue;

    for (let c = 0; c < word.text.length; c++) {
      if (currentCharIndex >= controller.currentCharCount) {
        word.visibleText = word.text.substring(0, c + 1);

        controller.textObject.isDirty = true;

        controller.timeSinceLastCharTyped -= controller.timeBetweenChars;
        controller.currentCharCount++;
        if (w === tObj.words.length - 1 && c == word.text.length - 1) {
          break;
        }
        noTimeLeft = controller.timeSinceLastCharTyped < controller.timeBetweenChars;
      }
      currentCharIndex++;
      if (noTimeLeft) break;
    }
    if (noTimeLeft) {
      break;
    }
  }
  if (!noTimeLeft) {
    //if we reached the end, end there's still time left, we're done
    controller.currentTextDone = true;
  }

  const isLastText = controller.currentTextIndex === controller.textArray.length - 1;

  if (controller.currentTextDone && isLastText) {
    controller.doKill = true;
  }
}

function update(tObj: TextObject): boolean {
  let isDirty = false;

  let { state, progress } = getEntityStateAndProgress(tObj);

  if (state === VisibilityState.APPEARING) {
    // if (tObj.scale !== 1) {
    //   tObj.scale = 1;
    // }
    // if (tObj.scale !== 1) {
    //   tObj.scale = 1;
    // }

    switch (tObj.appearStyle) {
      case TextAppearStyle.FADE:
        textFade(tObj, progress);

        break;
      case TextAppearStyle.FADE_AND_MOVE_RIGHT:
        textFade(tObj, progress);
        textMove(tObj, progress, v2(20, 0));
        break;
      case TextAppearStyle.FADE_AND_MOVE_LEFT:
        textFade(tObj, progress);
        textMove(tObj, progress, v2(-20, 0));
        break;
      case TextAppearStyle.FADE_AND_MOVE_UP:
        textFade(tObj, progress);
        textMove(tObj, progress, v2(0, -20));
        break;
      case TextAppearStyle.FADE_AND_MOVE_DOWN:
        textFade(tObj, progress);
        textMove(tObj, progress, v2(0, 20));
        break;
      default:
        textFade(tObj, 1);
        textMove(tObj, 1, v2());
        appearUnfold(tObj, progress);
        break;
    }
    isDirty = true;
  } else if (state === VisibilityState.DISAPPEARING || state === VisibilityState.HIDING) {
    switch (tObj.appearStyle) {
      case TextAppearStyle.FADE:
      case TextAppearStyle.FADE_AND_MOVE_RIGHT:
      case TextAppearStyle.FADE_AND_MOVE_LEFT:
      case TextAppearStyle.FADE_AND_MOVE_UP:
      case TextAppearStyle.FADE_AND_MOVE_DOWN:
        // if (tObj.scale !== 1) {
        //   tObj.scale = 1;
        // }

        textFade(tObj, 1 - progress);
        break;
      default:
        if (tObj.disappearAnimation) {
          tObj.disappearAnimation(tObj, progress);
        }
    }
    isDirty = true;
  } else if (state === VisibilityState.DEFAULT) {
    // if (tObj.scale !== 1) {
    //   tObj.scale = 1;
    // }

    if (tObj.currentTextFade !== 1) {
      textFade(tObj, 1);
      textMove(tObj, progress, v2());
      isDirty = true;
    }
  } else if (state === VisibilityState.HIDDEN) {
    // if (tObj.scale !== 0) {
    //   tObj.scale = 0;
    // }

    if (tObj.currentTextFade !== 0) {
      textFade(tObj, 0);
      isDirty = true;
    }
  }

  if (tObj.closeBtn && tObj.closeBtn.clickedThisFrame) {
    setVisibilityState(tObj, VisibilityState.HIDING);
  }

  //check at which word/character the cursor is
  if (state === VisibilityState.DEFAULT) {
    const textEnt = tObj.textEnt;

    if (!tObj.hideAllText && tObj.hoverWords && tObj.mouseOver) {
      let hoverBlock: TextBlock = null;
      let hoverLine: TextLine = null;
      let hoverWord: Word = null;

      const textEntPosX = tObj.relativeMousePos.x - textEnt.pos.x;
      const textEntPosY = tObj.relativeMousePos.y - textEnt.pos.y;
      for (let i = 0; i < tObj.textEnt.textBlocks.length; i++) {
        const block = tObj.textEnt.textBlocks[i];
        if (
          !hoverBlock &&
          textEntPosY >= block.pos.y &&
          textEntPosY < block.pos.y + block.height &&
          textEntPosX >= block.pos.x &&
          textEntPosX < block.pos.x + block.width
        ) {
          hoverBlock = block;
          block.hover = true;
          if (textEnt.hoverBlock !== hoverBlock) {
            if (textEnt.hoverBlock) {
              textEnt.hoverBlock.hover = false;
            }
            textEnt.hoverBlock = hoverBlock;
          }
          break;
        }
      }

      if (hoverBlock) {
        let blockPosY = textEntPosY - hoverBlock.pos.y;
        let blockPosX = textEntPosX - hoverBlock.pos.x;
        for (let i = 0; i < textEnt.hoverBlock.lines.length; i++) {
          const line = hoverBlock.lines[i];

          if (
            !hoverLine &&
            blockPosY >= line.localPos.y &&
            blockPosY < line.localPos.y + line.height &&
            blockPosX >= line.localPos.x &&
            blockPosX < line.localPos.x + line.width
          ) {
            hoverLine = line;
            line.hover = true;
            if (textEnt.hoverLine !== hoverLine) {
              if (textEnt.hoverLine) {
                textEnt.hoverLine.hover = false;
              }
              textEnt.hoverLine = hoverLine;
            }
            break;
          }
        }
      } else {
        if (textEnt.hoverBlock) {
          textEnt.hoverBlock.hover = false;
          textEnt.hoverBlock = null;
        }
        if (textEnt.hoverLine) {
          textEnt.hoverLine.hover = false;
          textEnt.hoverLine = null;
        }
        if (textEnt.hoverWord) {
          stopWordHover(tObj, textEnt.hoverWord);
          textEnt.hoverWord.hover = false;
          textEnt.hoverWord = null;
        }
      }

      if (hoverLine) {
        let linePosY = textEntPosY - hoverBlock.pos.y - hoverLine.localPos.y;
        let linePosX = textEntPosX - hoverBlock.pos.x - hoverLine.localPos.x;
        for (let i = 0; i < hoverLine.words.length; i++) {
          const word = hoverLine.words[i];
          if (
            !hoverWord &&
            linePosY >= word.localPos.y &&
            linePosY < word.localPos.y + word.height &&
            linePosX >= word.localPos.x &&
            linePosX < word.localPos.x + word.width
          ) {
            hoverWord = word;
            word.hover = true;

            if (textEnt.hoverWord !== hoverWord) {
              if (textEnt.hoverWord) {
                textEnt.hoverWord.hover = false;
                stopWordHover(tObj, textEnt.hoverWord);
              }
              textEnt.hoverWord = hoverWord;
              if (textEnt.hoverWord.command) {
                startWordHover(tObj, textEnt.hoverWord);
              }
            }
            break;
          }
        }
      } else {
        if (textEnt.hoverLine) {
          textEnt.hoverLine.hover = false;
          textEnt.hoverLine = null;
        }
        if (textEnt.hoverWord) {
          stopWordHover(tObj, textEnt.hoverWord);
          textEnt.hoverWord.hover = false;
          textEnt.hoverWord = null;
        }
      }
    } else {
      if (textEnt.hoverBlock) {
        textEnt.hoverBlock.hover = false;
        if (textEnt.hoverLine) textEnt.hoverLine.hover = false;
        if (textEnt.hoverWord) textEnt.hoverWord.hover = false;
        textEnt.hoverBlock = null;
        textEnt.hoverLine = null;
        if (textEnt.hoverWord) {
          stopWordHover(tObj, textEnt.hoverWord);
          textEnt.hoverWord.hover = false;
          textEnt.hoverWord = null;
        }
      }
    }
  }

  if (tObj.clickedThisFrame) {
    if (!tObj.allowEdit && tObj.textEnt.hoverWord && tObj.textEnt.hoverWord.command) {
      executeCommand(tObj.textEnt.hoverWord.command, tObj.manager);
    }
  }

  if (tObj.linkPopup && tObj.linkPopupDisappearTime > 0) {
    if (tObj.linkPopupDisappearTime > 0.5 && !tObj.linkPopup.mouseOver) {
      setVisibilityState(tObj.linkPopup, VisibilityState.DISAPPEARING);
      tObj.linkPopup = null;
      tObj.linkPopupDisappearTime = 0;
    }
    tObj.linkPopupDisappearTime += dt;
  }

  for (let hc = tObj.hoverContainers.length - 1; hc >= 0; hc--) {
    const actionId = tObj.hoverContainers[hc].actionId;
    if (tObj.hoverContainers[hc].container.startHighlightThisFrame) {
      // tObj.hoverContainers[hc].word.colorAlpha = 255;

      setContainerStyle(tObj.hoverContainers[hc].container, SHAPE_STYLES.DEBUG);
      const linkedContainers = tObj.hoverContainers.filter((it) => it.actionId === actionId);
      for (let k = 0; k < linkedContainers.length; k++) {
        setContainerStyle(linkedContainers[k].container, SHAPE_STYLES.DEBUG);
      }
    } else if (tObj.hoverContainers[hc].container.stopHighlightThisFrame) {
      setContainerStyle(tObj.hoverContainers[hc].container, null);
      const linkedContainers = tObj.hoverContainers.filter((it) => it.actionId === actionId);
      for (let k = 0; k < linkedContainers.length; k++) {
        setContainerStyle(linkedContainers[k].container, null);
      }
    }
  }

  return isDirty;
}

function appearUnfold(tObj: TextObject, p: number) {
  if (tObj.shape === Shape.CIRCLE) {
    // tObj.scale = p;
  } else {
    let minHeightScale = 32 / tObj.height;
    let growHeightScale = 1 - minHeightScale;
    let t = remapNumber01(p, 0.5, 1, true);
    // tObj.scale = minHeightScale + t * growHeightScale;
    // tObj.widthScale = remapNumber01(p, 0, 0.5, true);
    // if (isNaN(tObj.widthScale)) {
    //   debugState.doDebugSkip = true;
    // }
  }
}

export function textFade(tObj: TextObject, p: number) {
  for (let i = tObj.words.length - 1; i >= 0; i--) {
    tObj.words[i].colorAlpha = p * 255;

    // tObj.textStyle.color[4] =
  }
  tObj.backgroundAlpha = p * 255;

  tObj.currentTextFade = p;
}

export function textMove(tObj: TextObject, p: number, offset: V2) {
  const e = Ease.outCubic(p);
  const move = scaleV2(offset, -1 + e);

  tObj.textEnt.posOffset = move;
}

export function startAutoTypeController(tObj: TextObject) {
  if (tObj.autoTypeController) {
    tObj.autoTypeController.doKill = true;
  }
  tObj.autoTypeController = createAutoTypeController(tObj, [tObj.text]);
  tObj.manager.autoTypeControllers.push(tObj.autoTypeController);
}

export function getCharIndexAtXPos(
  word: Word,
  xPos: number,
  fontSizePx: number,
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
) {
  const charArray = word.text.split('');

  let textPart: string = '';
  let lastLength = 0;

  for (let i = 0; i < charArray.length; i++) {
    const textToTry = textPart + charArray[i];

    const length = TextLayoutController.getTextWidth(
      ctx,
      word.wordStyle.fontId,
      fontSizePx,
      textToTry,
    );

    const diff = Math.abs(length - xPos);

    if (length > xPos) {
      const charLength = length - lastLength;

      if (diff / charLength < 0.5) {
        return i + 1;
      }
      return i;
    } else {
      textPart = textToTry;
      lastLength = length;
    }
  }
}

function startWordHover(tObj: TextObject, word: Word) {
  word.textColor = COLORS.BLUE_HIGHLIGHT;
  if (tObj.allowEdit) {
    if (word.command.value === 'external_link' && word.command.args.length) {
      const uri = word.command.args[0];

      if (tObj.linkPopup) {
        if (tObj.linkPopup.text !== uri) {
          setVisibilityState(tObj.linkPopup, VisibilityState.DISAPPEARING);
          tObj.linkPopup = null;
        }
      }

      if (!tObj.linkPopup) {
        tObj.linkPopup = createAndAddLocalTextObject(
          tObj.manager,
          getZeroStaticEntity(),
          uri,
          TEXT_STYLE.LINK_POPUP,
        );
        tObj.linkPopup.hoverWords = true;
        tObj.linkPopup.allowHighlight = true;
        tObj.linkPopup.clickable = true;
        setParent(tObj.linkPopup, tObj, v2());
        tObj.linkPopup.moveMode = MoveMode.ANCHORED;
        setVisibilityState(tObj.linkPopup, VisibilityState.APPEARING);
      }

      const offset = addV2(tObj.textEnt.pos, word.pos, v2(0, word.height), v2(0, 5));

      tObj.linkPopup.anchoredPos = anchoredPos(v2(0.5, 0.5), offset, v2(0, 0));

      tObj.linkPopupDisappearTime = 0;
    }
  }
}

function stopWordHover(tObj: TextObject, word: Word) {
  word.textColor = null;

  if (tObj.linkPopup) {
    tObj.linkPopupDisappearTime += dt;
  }
}
